/**
 * Copilot Service - API service for Microsoft 365 Copilot monitoring
 *
 * Connects to the Enterprise AI Microservice to fetch real data from OpenSearch.
 * The microservice queries OTEL traces for Copilot interactions.
 *
 * Also connects to the Compliance Microservice for framework data.
 */

import { enterpriseAIAPI } from '../api/enterpriseAIClient';
import { complianceAPI } from '../api/complianceClient';
import { managementService } from '../management/managementService';

// Distinct non-empty strings, order kept (picker option lists).
const uniqueStrings = (list) => [
  ...new Set((Array.isArray(list) ? list : []).map((v) => (v == null ? '' : String(v).trim())).filter(Boolean)),
];

// Resolve the user's organization_id. The rest of the app stores the
// logged-in user under `localStorage.user` (a JSON blob with
// organization_id inside) — see src/services/api/apiClient.js for the
// canonical pattern. JWT payload is a secondary fallback for callers
// that have a token without a separately persisted user object.
function _resolveOrgId() {
  try {
    const rawUser = localStorage.getItem('user');
    if (rawUser) {
      const user = JSON.parse(rawUser);
      const fromUser = user?.organization_id ?? user?.org_id ?? user?.organizationId;
      if (fromUser != null) return fromUser;
    }
  } catch {
    // fall through
  }
  try {
    const token = localStorage.getItem('jwt_token') || localStorage.getItem('access_token') || localStorage.getItem('token');
    if (!token) return undefined;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.organization_id ?? payload.org_id;
  } catch {
    return undefined;
  }
}

// Helper to get friendly app name. Mirrors the backend's
// _friendly_app_name (saf3ai-copilot-microservice) so the UI shows the
// same friendly name even when the backend returns raw IPM tags.
export function getAppDisplayName(appClass) {
  if (!appClass) return 'Unknown App';

  const appNames = {
    "ipm.skypeteams.message.copilot.bizchat": "Microsoft 365 Chat",
    "ipm.skypeteams.message.copilot.webchat": "Teams Web Chat",
    "ipm.skypeteams.message.copilot.outlook": "Outlook",
    "ipm.skypeteams.message.copilot.word": "Word",
    "ipm.skypeteams.message.copilot.excel": "Excel",
    "ipm.skypeteams.message.copilot.powerpoint": "PowerPoint",
    "ipm.skypeteams.message.copilot.teams": "Teams",
    "ipm.note.copilot": "Outlook",
    "ipm.document.copilot.word": "Word",
    "ipm.schedule.meeting.copilot": "Calendar",
    "ipm.appointment.copilot.excel": "Excel",
    "ipm.copilot.powerpoint": "PowerPoint",
    "bizchat": "Microsoft 365 Chat",
    "m365copilot": "Microsoft 365 Chat",
  };

  // Already-friendly names pass through
  const friendlyNames = ["Word", "Excel", "PowerPoint", "PDF", "Outlook", "OneNote", "SharePoint", "Teams", "Calendar", "Microsoft 365 Chat", "Teams Web Chat"];
  if (friendlyNames.includes(appClass)) {
    return appClass;
  }

  const lower = appClass.toLowerCase();
  if (appNames[lower]) return appNames[lower];

  // Suffix fallback for any IPM.*/copilot.* variant we haven't enumerated
  if (lower.startsWith("ipm.") || lower.includes("copilot.")) {
    const suffixHints = [
      ["outlook", "Outlook"],
      ["powerpoint", "PowerPoint"],
      ["excel", "Excel"],
      ["word", "Word"],
      ["teams", "Teams"],
      ["onenote", "OneNote"],
      ["sharepoint", "SharePoint"],
      ["bizchat", "Microsoft 365 Chat"],
      ["webchat", "Teams Web Chat"],
    ];
    for (const [hint, friendly] of suffixHints) {
      if (lower.endsWith(`.${hint}`) || lower.endsWith(hint)) return friendly;
    }
  }

  return appClass;
}

// Helper to format timestamp to relative time
function getRelativeTime(timestamp) {
  if (!timestamp) return 'Unknown';
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

// Microsoft 365 does not expose a reliable execution success/failure outcome.
// It does expose scanner findings, so Copilot surfaces the measurable share of
// interactions with no detected threat as a separate, accurately named metric.
function calculateThreatFreeRate(interactions, threats) {
  const total = Number(interactions);
  if (!Number.isFinite(total) || total <= 0) return null;

  const detected = Number(threats);
  if (threats === null || threats === undefined || !Number.isFinite(detected)) return null;
  const boundedThreats = Math.min(total, Math.max(0, detected));
  return Math.round(((total - boundedThreats) / total) * 1000) / 10;
}

export const copilotService = {
  /**
   * Resolve whether the tenant has an enabled Microsoft 365 Copilot
   * integration. Dashboard zeroes alone cannot distinguish a disconnected
   * source from a connected source with no activity in the selected period.
   */
  async getConnectionStatus() {
    const data = await managementService.getIntegrations();
    const integrations = Array.isArray(data) ? data : (data?.integrations || []);
    const configured = integrations.filter((integration) => (
      integration?.platform === 'copilot' && integration?.has_credentials === true
    ));

    return {
      connected: configured.length > 0,
      enabled: configured.some((integration) => integration.enabled !== false),
    };
  },

  /**
   * Get dashboard summary statistics
   */
  async getDashboardStats(params = {}) {
    try {
      const data = await enterpriseAIAPI.getDashboard(params);
      const applications = new Map();

      for (const [application, count] of Object.entries(data.application_distribution || {})) {
        const displayName = getAppDisplayName(application);
        applications.set(displayName, (applications.get(displayName) || 0) + (Number(count) || 0));
      }

      const totalInteractions = Number(data.total_interactions) || 0;
      const applicationTotal = Array.from(applications.values()).reduce((sum, n) => sum + n, 0);
      // Share of interactions per app. The dashboard's Value Breakdown splits
      // the productivity value by pct_of_total; without it every app showed
      // $0 (audit A15). Denominator is the sum of the app buckets so the
      // shares add up to 100 even when some spans carry no application.
      const shareBase = applicationTotal > 0 ? applicationTotal : totalInteractions;
      const agentBreakdown = Array.from(applications, ([agent_name, interactions]) => ({
        agent_name,
        interactions,
        pct_of_total: shareBase > 0 ? Math.round((interactions / shareBase) * 1000) / 10 : 0,
      }))
        .filter(application => application.interactions > 0)
        .sort((a, b) => b.interactions - a.interactions);

      const threatsDetected = Number(data.interactions_with_threats) || 0;
      const ungroundedAnswers = Math.max(0, Number(
        data.ungrounded_answers ?? data.knowledge_gaps ?? 0,
      ) || 0);
      // Grounding is measured only on spans that carry the analyzer verdict
      // (grounding_analyzed). Without that population the coverage is
      // unknown — total interactions is not a substitute denominator (A13).
      const groundingAnalyzed = data.grounding_analyzed == null
        ? null
        : Math.max(0, Number(data.grounding_analyzed) || 0);
      const derivedGroundingCoverage = groundingAnalyzed > 0
        ? Math.max(0, Math.min(1, (groundingAnalyzed - ungroundedAnswers) / groundingAnalyzed))
        : null;
      const groundingCoverage = data.avg_grounding_coverage == null
        ? derivedGroundingCoverage
        : Math.max(0, Math.min(1, Number(data.avg_grounding_coverage) || 0));

      // Map API response to expected format
      return {
        total_interactions: totalInteractions,
        user_prompts: Math.floor(totalInteractions / 2),
        ai_responses: Math.floor(totalInteractions / 2),
        threats_detected: threatsDetected,
        threat_free_rate: calculateThreatFreeRate(totalInteractions, threatsDetected),
        pii_detected: data.interactions_with_pii || 0,
        unique_users: data.unique_users || 0,
        unique_applications: Object.keys(data.application_distribution || {}).length,
        threat_breakdown: data.severity_distribution || { critical: 0, high: 0, medium: 0, none: 0 },
        severity_distribution: data.severity_distribution || {},
        application_distribution: data.application_distribution || {},
        timeline: data.timeline || [],
        critical_count: data.severity_distribution?.critical || 0,
        high_count: data.severity_distribution?.high || 0,
        medium_count: data.severity_distribution?.medium || 0,
        low_count: data.severity_distribution?.low || 0,
        knowledge_gaps: data.knowledge_gaps || 0,
        grounding_analyzed: groundingAnalyzed,
        grounded_answers: groundingAnalyzed == null
          ? null
          : Math.max(0, Number(data.grounded_answers ?? (groundingAnalyzed - ungroundedAnswers)) || 0),
        ungrounded_answers: ungroundedAnswers,
        avg_grounding_coverage: groundingCoverage,
        high_risk_intents: data.high_risk_intents || 0,
        active_agents: agentBreakdown.length,
        active_sessions: data.unique_sessions || 0,
        agent_breakdown: agentBreakdown,
        // Session & Semantic Intelligence card inputs (audit A14). Both come
        // from the ingestor's analyzers; an empty object means "not enriched"
        // and the card shows its empty state. M365 has no session-quality or
        // session-outcome source, so avg_session_quality /
        // session_outcome_distribution are deliberately not set.
        complexity_distribution: data.complexity_distribution || {},
        conversation_flow_distribution: data.conversation_flow_distribution || {},
        // Real average interaction duration from session data (in minutes)
        avg_minutes_per_interaction: data.avg_minutes_per_interaction || 5,
        total_session_duration_minutes: data.total_session_duration_minutes || 0,
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  },

  /**
   * Get all interactions with optional filtering
   */
  async getInteractions(params = {}) {
    try {
      const data = await enterpriseAIAPI.getInteractions({
        page: params.page || 1,
        page_size: params.limit || params.page_size || 20,
        user_email: params.user_email,
        application: params.application,
        severity: params.threat_severity,
        search: params.search,
        start_time: params.start_time,
        end_time: params.end_time,
      });

      // Map API response to expected format
      const interactions = (data.interactions || []).map(i => ({
        interaction_id: i.interaction_id,
        timestamp: i.timestamp,
        user_email: i.user_email,
        application: getAppDisplayName(i.application),
        session_id: i.session_id,
        interaction_type: i.intent_category || 'unknown',
        threat_severity: i.threat_severity || 'none',
        has_pii: i.has_pii || false,
        scan_status: i.scan_status,
      }));

      return {
        interactions,
        total: data.total || interactions.length,
        page: params.page || 1,
        limit: params.limit || 20,
        total_pages: Math.ceil((data.total || interactions.length) / (params.limit || 20)),
        stats: data.stats || {},
      };
    } catch (error) {
      console.error('Error fetching interactions:', error);
      throw error;
    }
  },

  /**
   * Get interactions with threats
   */
  async getThreats(params = {}) {
    try {
      const data = await enterpriseAIAPI.getThreats({
        severity: params.severity && params.severity !== 'all' ? params.severity : undefined,
        threat_type: params.threat_type && params.threat_type !== 'all' ? params.threat_type : undefined,
        user_email: params.user_email || undefined,
        agent_name: params.agent_name || undefined,
        page: params.page || 1,
        page_size: params.page_size || 50,
        start_time: params.start_time || undefined,
        end_time: params.end_time || undefined,
        include_trends: params.include_trends || undefined,
      });

      const threats = (data.threats || []).map((t) => {
        const appName = getAppDisplayName(t.application);
        // Unknown severity stays null (the pages drop it from severity
        // counts); it was defaulted to 'low' (audit B15).
        const severity = t.severity || t.threat_severity || null;
        return {
          id: t.interaction_id || t.id,
          conversation_id: t.interaction_id || t.conversation_id,
          interaction_id: t.interaction_id,
          timestamp: t.timestamp,
          // Shared threats page reads agent_name; M365 Copilot uses application.
          agent_name: t.agent_name || appName || 'Unknown App',
          application: appName,
          user_email: t.user_email || '',
          severity,
          // Keep legacy alias for any callers still reading threat_severity.
          threat_severity: severity,
          threat_type: t.threat_type || 'unknown',
          auto_flagged: t.auto_flagged || false,
          query: t.query || t.obfuscated_prompt_summary || '',
          obfuscated_prompt_summary: t.obfuscated_prompt_summary || '',
          obfuscated_response_summary: t.obfuscated_response_summary || '',
          intent_category: t.intent_category || '',
          detection_categories: t.detection_categories || [],
          classification_reason: t.classification_reason || t.detail || '',
          // M365 audit records carry no execution outcome: null = unknown
          // (Injection Patterns shows "Outcome unknown", not 0%/100%). B15.
          state: t.state || null,
          risk_factors: t.risk_factors || [],
          has_pii: t.has_pii || false,
          policy_violation: t.policy_violation || false,
          pii_types: Array.isArray(t.pii_types) ? t.pii_types : [],
          model_armor: t.model_armor || null,
          data_encrypted: t.data_encrypted,
          data_policy: t.data_policy,
          'saf3ai.encryption.wrapped_key': t['saf3ai.encryption.wrapped_key'],
        };
      });

      return {
        threats,
        total: data.total ?? null, // the server total, never the page length (D2)
        by_severity: data.severity_distribution || data.severity_counts || {},
        page: data.page || 1,
        total_pages: data.total_pages || 1,
        risk_policy: data.risk_policy || {},
        previous_period: data.previous_period || null,
      };
    } catch (error) {
      console.error('Error fetching threats:', error);
      throw error;
    }
  },

  /**
   * Get user activity summary
   */
  async getUsers(params = {}) {
    try {
      const data = await enterpriseAIAPI.getUsers({
        page: params.page || 1,
        page_size: params.page_size || 50,
        risk_level: params.risk_level,
        search: params.search,
        start_time: params.start_time,
        end_time: params.end_time,
      });

      const users = (data.users || []).map(u => {
        // Friendly app list (collapse IPM string + already-friendly into
        // one entry). Drives the "agents_used" count + applications_used
        // chips on the Top Users card.
        const friendlyApps = Array.from(new Set(
          (u.content_sources || u.applications || []).map(getAppDisplayName)
        ));
        return {
          user_id: u.user_email,
          user_email: u.user_email,
          // Several fields the shared Top Users card and CSV export read
          // — surface all aliases pointing at the same underlying count.
          interactions: u.interactions || 0,
          total_interactions: u.interactions || 0,
          sessions: u.sessions || u.session_count || u.unique_sessions || 0,
          agents_used: friendlyApps.length,
          prompts: u.interactions || 0,
          threats: u.threats || 0,
          critical_threats: u.critical_threats || 0,
          high_threats: u.high_threats || 0,
          medium_threats: u.medium_threats || 0,
          low_threats: u.low_threats || 0,
          // pages/copilot/users re-exports gemini/agents/users, whose
          // UserDetailPanel reads pii_detected — keep pii_incidents too since
          // the (unused-by-that-route) copilot-specific panel reads it.
          pii_detected: u.pii_detected || 0,
          pii_incidents: u.pii_detected || 0,
          high_risk_queries: u.high_risk_queries || 0,
          avg_risk_score: u.avg_risk_score || 0,
          // Microsoft 365 audit exports do not currently include an
          // execution outcome. Preserve a measured value if a future feed
          // supplies one, but never turn missing telemetry into 0%.
          success_rate: u.success_rate ?? null,
          applications_used: friendlyApps,
          document_types: u.document_types || [],
          last_activity: u.last_activity,
          risk_level: u.risk_level || 'none',
        };
      });

      return {
        users,
        total: data.total || users.length,
        by_risk: data.risk_counts || {
          high: users.filter(u => u.risk_level === 'high').length,
          medium: users.filter(u => u.risk_level === 'medium').length,
          low: users.filter(u => u.risk_level === 'low').length,
          none: users.filter(u => u.risk_level === 'none').length,
        },
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  /**
   * Get application usage breakdown
   */
  async getApplications(params = {}) {
    try {
      const data = await enterpriseAIAPI.getApplications(params);

      const applications = (data.applications || []).map(a => ({
        application: a.name,
        display_name: a.display_name || getAppDisplayName(a.name),
        total_interactions: a.interactions || 0,
        unique_users: a.users || 0,
        // Distinct sessions per app; null when the backend did not measure
        // it (apps inferred from sampled documents/mentions).
        sessions: a.sessions ?? null,
        threats: a.threats || 0,
        scans_completed: a.scans_completed ?? 0,
        threat_rate: a.threat_rate || 0,
        threat_free_rate: calculateThreatFreeRate(a.interactions, a.threats),
      }));

      return {
        applications,
        total: applications.length,
        summary: data.summary || {},
      };
    } catch (error) {
      console.error('Error fetching applications:', error);
      throw error;
    }
  },

  /**
   * Alias used by the shared Agent Registry page (gemini/agents/registry).
   * Maps M365 applications into the agent shape the registry UI expects.
   */
  async getAgents(params = {}) {
    try {
      const data = await this.getApplications(params);
      const agents = (data.applications || []).map(a => ({
        agent_name: a.application,
        display_name: a.display_name || a.application,
        engine_id: a.application,
        total_interactions: a.total_interactions || 0,
        // Registry "Total Sessions" — real per-app session cardinality, not
        // unique users (audit A16).
        unique_sessions: a.sessions,
        unique_users: a.unique_users || 0,
        // Keep execution success distinct from the security assessment. The
        // M365 feed does not publish execution outcomes.
        success_rate: null,
        threat_free_rate: a.threat_free_rate,
        source_domains: [],
        methods: [],
        model: null,
      }));
      return { agents };
    } catch (error) {
      console.error('Error fetching agents (M365):', error);
      throw error;
    }
  },

  /**
   * Get enhanced dashboard stats with security score and intent analysis
   */
  async getEnhancedDashboardStats(params = {}) {
    try {
      const [dashboardData, intentData, complianceData] = await Promise.all([
        enterpriseAIAPI.getDashboard(params),
        enterpriseAIAPI.getIntentAnalysis(params).catch(() => null),
        enterpriseAIAPI.getCompliance(params).catch(() => null),
      ]);

      // Calculate security score
      const total = dashboardData.total_interactions || 1;
      const threats = dashboardData.interactions_with_threats || 0;
      const pii = dashboardData.interactions_with_pii || 0;
      const critical = dashboardData.severity_distribution?.critical || 0;
      const high = dashboardData.severity_distribution?.high || 0;

      const threatPenalty = (threats / total) * 40;
      const piiPenalty = (pii / total) * 30;
      const criticalPenalty = critical * 5;
      const highPenalty = high * 3;
      const securityScore = Math.max(0, Math.round(100 - threatPenalty - piiPenalty - criticalPenalty - highPenalty));

      return {
        ...dashboardData,
        security_score: securityScore,
        security_grade: securityScore >= 90 ? 'A' : securityScore >= 80 ? 'B' : securityScore >= 70 ? 'C' : securityScore >= 60 ? 'D' : 'F',
        criticalCount: critical,
        highCount: high,
        mediumCount: dashboardData.severity_distribution?.medium || 0,
        lowCount: dashboardData.severity_distribution?.low || 0,
        intent_breakdown: intentData?.risk_distribution || {},
        jailbreak_attempts: intentData?.summary?.jailbreak_attempts || 0,
        risky_sessions: dashboardData.high_risk_intents || 0,
        total_sessions: dashboardData.unique_sessions || 0,
        // No compliance evidence → not assessed (null), never an invented 85.
        compliance_score: complianceData?.overall_score ?? null,
      };
    } catch (error) {
      console.error('Error fetching enhanced dashboard stats:', error);
      throw error;
    }
  },

  /**
   * Get session timeline with conversation threads
   */
  async getSessionTimeline(sessionId = null, params = {}) {
    try {
      const queryParams = { ...params, ...(sessionId ? { session_id: sessionId } : {}) };
      const data = await enterpriseAIAPI.getSessions(queryParams);

      const sessions = (data.sessions || []).map(s => ({
        session_id: s.session_id,
        user_email: s.user_email,
        application: getAppDisplayName(s.application),
        start_time: s.start_time,
        end_time: s.end_time,
        interaction_count: s.interactions || 0,
        // interactions is the message array (for conversation display), interaction_count is the number
        interactions: Array.isArray(s.interaction_details) ? s.interaction_details : [],
        threats_in_session: s.threats_in_session || 0,
        threat_count: s.threats_in_session || 0,
        risk_score: Math.round((s.risk_score || 0) * 100),
        has_pii: false,
        duration_minutes: s.start_time && s.end_time
          ? Math.round((new Date(s.end_time) - new Date(s.start_time)) / 60000) || 1
          : 1,
      }));

      return {
        sessions,
        total: data.total || sessions.length,
        high_risk_count: data.high_risk_count || sessions.filter(s => s.risk_score > 50).length,
        total_interactions: data.total_interactions || 0,
      };
    } catch (error) {
      console.error('Error fetching session timeline:', error);
      throw error;
    }
  },

  /**
   * Get intent analysis with classification
   */
  async getIntentAnalysis(params = {}) {
    try {
      const data = await enterpriseAIAPI.getIntentAnalysis(params);

      // Category summary for radar chart and category cards
      const categories = (data.categories || []).map(c => ({
        category: c.category,
        count: c.count,
        avg_confidence: c.avg_confidence || 0,
        high_risk_count: c.high_risk_count || 0,
      }));

      // Individual intent samples for the table display
      const intents = (data.intents || []).map(i => {
        const agentName = getAppDisplayName(i.agent_name || i.application);
        return {
          interaction_id: i.interaction_id,
          timestamp: i.timestamp,
          user_email: i.user_email,
          // The shared Intent Analysis view reads `agent_name` for the table,
          // filters, chart, and detail panel. Keep `application` as well for
          // Copilot-specific consumers.
          agent_name: agentName,
          application: agentName,
          category: i.category,
          confidence: i.confidence || 0,
          risk_level: (i.risk_level || 'none').toLowerCase(),
          is_risky: i.is_risky || false,
          indicators: i.indicators || [],
        };
      });

      return {
        intents,
        categories,
        summary: {
          total: data.summary?.total_analyzed || 0,
          high_risk: data.summary?.risky_intents || 0,
          jailbreak_attempts: data.summary?.jailbreak_attempts || 0,
        },
        risk_distribution: data.risk_distribution || {},
      };
    } catch (error) {
      console.error('Error fetching intent analysis:', error);
      throw error;
    }
  },

  /**
   * Get knowledge gaps analysis
   */
  async getKnowledgeGaps(params = {}) {
    try {
      const data = await enterpriseAIAPI.getKnowledgeGaps(params);
      const agentName = 'Microsoft 365 Copilot';

      const departmentGaps = (data.departments || []).map(d => ({
        topic: d.department,
        department: d.department,
        query_count: Number(d.total_gaps) || 0,
        // Departments only report a high-priority count; anything else is
        // unknown, not "medium".
        priority: d.high_priority > 0 ? 'high' : null,
        status: 'identified',
        agent_name: agentName,
        agents: [agentName],
      }));

      const topTopics = (data.top_topics || []).map(t => ({
        topic: t.topic,
        query_count: Number(t.count) || 0,
        // Real dominant priority from the backend (was stripped by the
        // response model, so every row read "medium" — A13); null = unknown.
        priority: t.priority || null,
        status: 'identified',
        agent_name: agentName,
        agents: [agentName],
      }));

      // Departments and topics are two groupings of the same gap records.
      // Concatenating them double-counted affected queries (27 became 42).
      // Topics are the row dimension expected by the shared page; departments
      // remain a fallback for older responses that do not include topics.
      const allGaps = topTopics.length > 0 ? topTopics : departmentGaps;
      // summary.total_gaps is the number of topics, not queries, so it is
      // not a fallback for ungrounded_queries.
      const ungroundedQueries = Math.max(0, Number(
        data.summary?.ungrounded_queries
          ?? allGaps.reduce((sum, gap) => sum + gap.query_count, 0),
      ) || 0);
      const representedQueries = allGaps.reduce((sum, gap) => sum + gap.query_count, 0);
      if (representedQueries < ungroundedQueries) {
        allGaps.push({
          topic: 'Uncategorized',
          query_count: ungroundedQueries - representedQueries,
          priority: null,
          status: 'identified',
          agent_name: agentName,
          agents: [agentName],
        });
      }
      const summary = {
        ...(data.summary || {}),
        total_gaps: allGaps.length,
        ungrounded_queries: ungroundedQueries,
      };

      return {
        gaps: allGaps,
        total: allGaps.length,
        stats: summary,
        summary,
        gap_type_distribution: data.gap_types || {},
        by_status: {
          missing: data.gap_types?.missing_documentation || 0,
          outdated: data.gap_types?.outdated_info || 0,
          incomplete: data.gap_types?.no_answer || 0,
        },
        by_priority: data.priorities || {
          critical: data.summary?.high_priority || 0,
          high: data.summary?.high_priority || 0,
          medium: data.summary?.medium_priority || 0,
        },
      };
    } catch (error) {
      console.error('Error fetching knowledge gaps:', error);
      throw error;
    }
  },

  /**
   * Get prompt pattern analysis
   */
  async getPromptPatterns(params = {}) {
    try {
      // Derive from intent analysis
      const intentData = await enterpriseAIAPI.getIntentAnalysis(params).catch(() => null);

      const categories = intentData?.categories || [];
      const total = categories.reduce((sum, c) => sum + c.count, 0) || 0;

      // Calculate prompt type percentages from actual categories
      const codeGen = categories.find(c => c.category === 'code_generation')?.count || 0;
      const infoSeek = categories.find(c => c.category === 'information_seeking')?.count || 0;
      const docAnalysis = categories.find(c => c.category === 'document_analysis')?.count || 0;
      const summarization = categories.find(c => c.category === 'summarization')?.count || 0;

      // Map to display categories: Code Generation, Information Query, Document Analysis/Writing
      // Document Analysis combines document_analysis + summarization
      const documentTotal = docAnalysis + summarization;

      return {
        patterns: {
          question_types: { what: 0, how: 0, why: 0, list: 0, summarize: 0, other: 0 },
          action_verbs: {},
          sensitive_queries: [],
          document_requests: [],
          avg_prompt_length: 0,
          prompt_complexity: { simple: 0, moderate: 0, complex: 0 },
        },
        total_prompts: total,
        code_prompts: total > 0 ? Math.round((codeGen / total) * 100) : 0,
        query_prompts: total > 0 ? Math.round((infoSeek / total) * 100) : 0,
        creative_prompts: total > 0 ? Math.round((documentTotal / total) * 100) : 0,
        code_count: codeGen,
        query_count: infoSeek,
        creative_count: documentTotal,
        // Also provide the actual categories for more detailed view
        categories: categories,
        insights: {
          most_common_type: categories.length > 0 ? [categories[0].category, categories[0].count] : ['none', 0],
          sensitive_query_rate: '0',
          document_access_rate: '0',
        },
      };
    } catch (error) {
      console.error('Error fetching prompt patterns:', error);
      throw error;
    }
  },

  /**
   * Get session flows for conversation reconstruction
   */
  async getSessionFlows(params = {}) {
    try {
      const data = await enterpriseAIAPI.getSessions({ page_size: 20, ...params });

      const sessions = (data.sessions || []).map(s => ({
        session_id: s.session_id,
        user: s.user_email || 'Unknown',  // Show full email
        user_email: s.user_email || '',   // Keep for navigation
        app: getAppDisplayName(s.application),
        application: getAppDisplayName(s.application),
        messages: [],
        start_time: s.start_time,
        end_time: s.end_time,
        duration_sec: s.start_time && s.end_time
          ? Math.round((new Date(s.end_time) - new Date(s.start_time)) / 1000)
          : 0,
        message_count: s.interactions || 0,
        has_threat: (s.threats_in_session || 0) > 0,
        has_pii: false,
        risk_score: s.risk_score || 0,
        time: getRelativeTime(s.end_time || s.start_time),
      }));

      // Also provide flows in a different format for dashboard
      const flows = sessions.map(s => ({
        user: s.user,
        user_email: s.user_email,  // Include full email for navigation
        application: s.app,
        has_threat: s.has_threat,
        time: s.time,
      }));

      return {
        sessions,
        flows,
        total: data.total || sessions.length,
        high_risk_sessions: sessions.filter(s => s.risk_score >= 50).length,
        sessions_with_refusals: 0,
        sessions_with_sensitive_docs: 0,
      };
    } catch (error) {
      console.error('Error fetching session flows:', error);
      throw error;
    }
  },

  /**
   * Get data exposure insights
   */
  async getDataExposureInsights(params = {}) {
    try {
      const dashboardData = await enterpriseAIAPI.getDashboard(params);

      return {
        document_access: [],
        total_document_accesses: 0,
        file_type_breakdown: {},
        sensitivity_label_usage: {},
        pii_exposures: [],
        pii_count: dashboardData.interactions_with_pii || 0,
        // M365 audit records carry no sensitivity labels, so these classes
        // are not measured (they were 30% of PII / 20% of traffic — B23).
        confidential_count: null,
        internal_count: null,
        safe_count: (dashboardData.total_interactions || 0) - (dashboardData.interactions_with_threats || 0),
        high_sensitivity_accesses: 0,
        risk_indicators: {
          sensitive_docs_accessed: 0,
          pii_in_prompts: dashboardData.interactions_with_pii || 0,
          threats_with_docs: 0,
        },
      };
    } catch (error) {
      console.error('Error fetching data exposure:', error);
      throw error;
    }
  },

  /**
   * Get real-time activity feed
   */
  async getActivityFeed(params = {}) {
    try {
      // Get both interactions and dashboard data for timeline
      const [interactionsData, dashboardData] = await Promise.all([
        enterpriseAIAPI.getInteractions({ page_size: 15, ...params }),
        enterpriseAIAPI.getDashboard(params).catch(() => null),
      ]);

      const feed = (interactionsData.interactions || []).map(i => {
        // Check for threats from BOTH scanner and intent classifier
        const hasScanThreat = i.threat_severity && i.threat_severity !== 'none';
        const hasIntentThreat = i.risk_level && ['critical', 'high'].includes(i.risk_level);
        const isThreat = hasScanThreat || hasIntentThreat;
        const severity = i.threat_severity || i.risk_level || 'none';

        return {
          id: i.interaction_id,
          type: i.intent_category || 'interaction',
          user: i.user_email || 'Unknown',  // Show full email
          user_email: i.user_email || '',   // Keep for navigation
          app: getAppDisplayName(i.application),
          preview: i.intent_category?.replace(/_/g, ' ') || 'Copilot interaction',
          timestamp: i.timestamp,
          is_threat: isThreat,
          threat_severity: severity,
          time_ago: getRelativeTime(i.timestamp),
        };
      });

      // Format for dashboard display
      const items = feed.map(f => ({
        user: f.user,
        user_email: f.user_email,  // Keep full email for navigation
        preview: f.preview,
        time_ago: f.time_ago,
        is_threat: f.is_threat,
      }));

      // Surface the FULL daily timeline (not just last 7) so the
      // dashboard's activity chart can match whatever date range the
      // user picked instead of always showing 7 days.
      const timeline = dashboardData?.timeline || [];
      const dailyCounts = timeline.map(t => t.count);
      const dailyDates = timeline.map(t => t.date);

      return {
        feed,
        items,
        hourly_activity: [],
        current_active_users: new Set(feed.slice(0, 5).map(f => f.user)).size,
        threats_last_hour: feed.filter(f => f.is_threat).length,
        dailyCounts,
        dailyDates,
      };
    } catch (error) {
      console.error('Error fetching activity feed:', error);
      throw error;
    }
  },

  /**
   * Get comprehensive risk signals
   */
  async getRiskSignals(params = {}) {
    try {
      const [threatsData, usersData] = await Promise.all([
        enterpriseAIAPI.getThreats({ page_size: 20, ...params }),
        enterpriseAIAPI.getUsers({ page_size: 10, ...params }),
      ]);

      const signals = (threatsData.threats || []).map(t => ({
        type: t.intent_category || 'threat',
        severity: t.severity || 'medium',
        user: t.user_email,
        timestamp: t.timestamp,
        detail: `${t.intent_category || 'Threat'} detected`,
        prompt_preview: '',
      }));

      const userRisks = (usersData.users || []).map(u => ({
        user: u.user_email,
        risk_score: u.avg_risk_score || 0,
        breakdown: {
          threats: u.threats || 0,
          pii: u.pii_detected || 0,
          high_risk: u.high_risk_queries || 0,
        },
      })).sort((a, b) => b.risk_score - a.risk_score);

      return {
        signals: signals.slice(0, 20),
        total_signals: threatsData.total || signals.length,
        by_severity: threatsData.severity_counts || {
          critical: signals.filter(s => s.severity === 'critical').length,
          high: signals.filter(s => s.severity === 'high').length,
          medium: signals.filter(s => s.severity === 'medium').length,
        },
        user_risks: userRisks,
        top_risk_user: userRisks[0] || null,
      };
    } catch (error) {
      console.error('Error fetching risk signals:', error);
      throw error;
    }
  },

  /**
   * Get ROI and usage attribution
   */
  /**
   * Token economics for this module.
   *
   * Returns the shared Tokenomics contract. Cost fields come back null — this
   * surface is not billed per token — and the `capabilities` block tells the
   * shared page which cards to render.
   */
  async getTokenomics(params = {}) {
    const timeParams = {};
    if (params.start_time) timeParams.start_time = params.start_time;
    if (params.end_time) timeParams.end_time = params.end_time;
    if (params.agent_name) timeParams.agent_name = params.agent_name;
    if (params.user_email) timeParams.user_email = params.user_email;
    return enterpriseAIAPI.getTokenomics(timeParams);
  },

  async getUsageROI(arg1 = null, arg2 = {}) {
    try {
      // Two calling conventions are supported:
      //   - Legacy: getUsageROI(config, params)
      //   - Gemini-shaped: getUsageROI({ start_time, end_time, include_trends })
      // The shared usage-roi component uses the second; the legacy one
      // is still hit by the dashboard tile. Detect by looking for
      // date-shaped keys on arg1.
      const arg1IsParams = arg1 && (
        'start_time' in arg1 || 'end_time' in arg1 ||
        'include_trends' in arg1 || 'time_range' in arg1
      );
      const params = arg1IsParams ? arg1 : arg2;
      let config = arg1IsParams ? null : arg1;
      if (!config) {
        // The gemini caller doesn't pass config — pull it ourselves so
        // ROI math (cost rate, license count) reflects MySQL settings.
        try { config = await this.getConfig(); } catch { config = null; }
      }

      const [dashboardData, usersData, appsData] = await Promise.all([
        enterpriseAIAPI.getDashboard(params),
        enterpriseAIAPI.getUsers({ page_size: 10, ...params }),
        enterpriseAIAPI.getApplications(params),
      ]);

      // Use config values if provided, otherwise defaults.
      // The Settings UI writes the license count to `roi.licensed_users`
      // and per-user cost to `roi.google_license_cost` (the field is shared
      // with Gemini Enterprise; the name is misleading but it's the
      // canonical source). `licensing.*` is a stale/parallel field that
      // never gets populated by the UI — we fall back to it only for
      // safety with old rows.
      const roiConfig = config?.roi || {};
      const licensingConfig = config?.licensing || {};
      const totalLicenses = parseInt(roiConfig.licensed_users) || licensingConfig.total_licenses || 0;
      const costPerUser = parseFloat(roiConfig.google_license_cost) || licensingConfig.cost_per_license || 30;
      const hourlyCostRate = parseFloat(roiConfig.hourly_cost_rate) || 50;
      const minutesPerQuery = parseFloat(roiConfig.avg_minutes_per_interaction) || 5;
      const valuePerInteraction = (minutesPerQuery / 60) * hourlyCostRate;

      const uniqueUsers = dashboardData.unique_users || 0;
      // Cost is the seat cost, not active-user cost — empty seats are still
      // paid for. Falling back to active users when no license count is set
      // makes ROI look artificially massive (small denominator).
      const totalCost = totalLicenses * costPerUser;

      // Time-saved value (matches Gemini Enterprise's productivity_value):
      //   interactions × minutes_per_query / 60 × hourly_cost
      const totalInteractions = dashboardData.total_interactions || 0;
      const estimatedValue = Math.round(totalInteractions * valuePerInteraction);

      const roi = totalCost > 0 ? ((estimatedValue - totalCost) / totalCost * 100).toFixed(1) : 0;

      // Complexity-weighted value — mirror gemini-enterprise's calculation
      // so the shared UI can show complexity_adjusted_value alongside the
      // simple per-interaction estimate. Weights match the gemini service:
      // simple=1, moderate=2, complex=4, expert=8.
      const COMPLEXITY_WEIGHTS = { simple: 1, moderate: 2, complex: 4, expert: 8 };
      const rawComplexity = { ...(dashboardData.complexity_distribution || {}) };
      // Backfill the unclassified remainder at weight ×1, exactly as the Gemini
      // Enterprise backend does. Only a fraction of M365 interactions carry a
      // complexity tier — 114 of 725 in a representative window — so weighting
      // just the classified ones made "Complexity-Adjusted Value" describe 16%
      // of the traffic while "Hours Saved" beside it described all of it, and
      // the two cards disagreed fivefold ($608 against the $3,021 its own hours
      // imply). Every interaction saved time whether or not it got a tier.
      const classifiedCount = Object.values(rawComplexity).reduce((s, c) => s + (c || 0), 0);
      const uncategorizedCount = Math.max(0, totalInteractions - classifiedCount);
      if (uncategorizedCount > 0) rawComplexity.uncategorized = uncategorizedCount;
      const totalComplexityCount = Object.values(rawComplexity).reduce((s, c) => s + (c || 0), 0);
      const complexityDistribution = Object.entries(rawComplexity).map(([tier, count]) => {
        const weight = COMPLEXITY_WEIGHTS[tier] || 1;
        const pct = totalComplexityCount > 0 ? Math.round((count / totalComplexityCount) * 100) : 0;
        return {
          tier,
          count,
          pct,
          weight,
          weighted_value: Math.round(count * weight * valuePerInteraction),
        };
      }).sort((a, b) => (COMPLEXITY_WEIGHTS[a.tier] || 0) - (COMPLEXITY_WEIGHTS[b.tier] || 0));
      const totalWeightedCount = complexityDistribution.reduce(
        (s, c) => s + (c.count * c.weight), 0,
      );
      const avgComplexityWeight = totalComplexityCount > 0
        ? +(totalWeightedCount / totalComplexityCount).toFixed(2)
        : 1;
      const complexityAdjustedValue = Math.round(totalWeightedCount * valuePerInteraction);

      // Department usage from applications
      // Hours use the same configured minutes-per-query as the value, not a
      // fixed interactions/10.
      const departmentUsage = (appsData.applications || []).slice(0, 5).map(a => ({
        department: a.display_name || a.name,
        hours: Math.round(((a.interactions || 0) * minutesPerQuery) / 60),
        interactions: a.interactions,
        value: Math.round(a.interactions * valuePerInteraction),
      }));

      // Top users - include more data for detail panel
      const topUsers = (usersData.users || []).slice(0, 5).map(u => {
        const interactions = u.total_interactions || u.interactions || 0;
        return {
          email: u.user_email,
          interactions: interactions,
          hours_saved: Math.round((interactions * minutesPerQuery) / 60),
          value_generated: Math.round(interactions * valuePerInteraction),
          risk_level: u.risk_level || 'none',
          avg_risk_score: u.avg_risk_score || 0,
          // Map applications_used to app_usage format for detail panel. The
          // users endpoint lists the apps but not per-app counts, so the
          // count is unknown (null) unless the backend supplies one — it was
          // interactions/3 (B23).
          app_usage: (u.applications_used || []).map(app => ({
            name: typeof app === 'string' ? app : (app.display_name || app.name || 'Unknown'),
            count: typeof app === 'string' ? null : (app.count ?? null),
          })),
        };
      });

      return {
        total_cost: totalCost,
        estimated_value: estimatedValue,
        roi_percentage: parseFloat(roi),
        unique_users: uniqueUsers,
        cost_per_user: costPerUser,
        // No per-category or per-month value source exists for M365. These
        // were a fixed 40/25/20/15 split and synthetic ramp-up weights (B23);
        // empty means "not measured" and consumers render their empty state.
        value_breakdown: [],
        department_usage: departmentUsage,
        top_users: topUsers,
        monthly_trend: [],
        total_licenses: totalLicenses,
        adoption_rate: totalLicenses > 0 ? Math.min(100, Math.round((uniqueUsers / totalLicenses) * 100)) : null,
        active_users_trend: [],
        // Query-complexity breakdown emitted by SemanticEnricher in the
        // ingestor's analyzer pipeline. Used by the Usage & ROI page to
        // surface complexity-weighted value alongside flat-rate estimate.
        complexity_distribution: complexityDistribution,
        complexity_adjusted_value: complexityAdjustedValue,
        avg_complexity_weight: avgComplexityWeight,
        avg_complexity_score: dashboardData.avg_complexity_score || 0,
        // ── Per-app rollup for the Usage by App / App Leaderboard /
        // Value Attribution charts. The shared gemini ROI component
        // reads this from ``roiData.agent_breakdown``; we mirror the
        // shape with the M365 apps list so all per-app charts populate.
        // Merge by friendly name so raw IPM.* tags don't show as a
        // separate row from the human-readable variant.
        agent_breakdown: (() => {
          const merged = new Map();
          for (const a of (appsData.applications || [])) {
            const friendly = a.display_name || getAppDisplayName(a.name);
            const interactions = a.interactions || 0;
            const threats = a.threats || 0;
            const existing = merged.get(friendly);
            if (existing) {
              existing.interactions += interactions;
              existing.threats += threats;
              existing.unique_users = Math.max(existing.unique_users, a.users || 0);
            } else {
              merged.set(friendly, {
                agent_name: friendly,
                agent_id: friendly,
                interactions,
                threats,
                // Retain the unavailable execution metric for consumers that
                // explicitly need it; Copilot UI surfaces threat_free_rate.
                success_rate: a.success_rate ?? null,
                unique_users: a.users || 0,
              });
            }
          }
          return Array.from(merged.values()).map(row => ({
            ...row,
            threat_free_rate: calculateThreatFreeRate(row.interactions, row.threats),
            pct_of_total: totalInteractions > 0
              ? Math.round((row.interactions / totalInteractions) * 100)
              : 0,
            value: Math.round(row.interactions * valuePerInteraction),
          }));
        })(),
        // Active "agents" for M365 = number of distinct friendly apps
        // with at least one interaction in window.
        active_agents: (() => {
          const friendlySet = new Set();
          for (const a of (appsData.applications || [])) {
            if ((a.interactions || 0) > 0) {
              friendlySet.add(a.display_name || getAppDisplayName(a.name));
            }
          }
          return friendlySet.size;
        })(),
        total_interactions: totalInteractions,
        // The gemini usage-roi component reads stats off a top-level
        // ``stats`` object — emit it alongside flat fields for parity.
        stats: {
          total_cost: totalCost,
          productivity_value: estimatedValue,
          complexity_adjusted_value: complexityAdjustedValue,
          avg_complexity_weight: avgComplexityWeight,
          hourly_cost_rate: hourlyCostRate,
          roi_percentage: parseFloat(roi),
          unique_users: uniqueUsers,
          total_interactions: totalInteractions,
          threat_free_rate: calculateThreatFreeRate(
            totalInteractions,
            dashboardData.interactions_with_threats,
          ),
          active_agents: (appsData.applications || []).filter(a => (a.interactions || 0) > 0).length,
          // Complexity-weighted, like Gemini Enterprise's. Hours and the
          // complexity-adjusted dollar figure sit next to each other on the
          // card row, so they have to be the same quantity in different units:
          // hours × rate must reproduce the value. A flat interaction count
          // here would not, now that the value is weighted.
          estimated_hours_saved: Math.round(
            (totalWeightedCount * minutesPerQuery) / 60,
          ),
          minutes_per_query: minutesPerQuery,
          unique_sessions: dashboardData.unique_sessions || 0,
          avg_queries_per_session: dashboardData.unique_sessions > 0
            ? +(totalInteractions / dashboardData.unique_sessions).toFixed(1)
            : 0,
        },
      };
    } catch (error) {
      console.error('Error fetching usage ROI:', error);
      throw error;
    }
  },

  /**
   * Get AI-specific compliance data
   * Fetches frameworks from the Compliance Microservice (OpenSearch) and enriches with violation metrics
   */
  async getAICompliance(params = {}) {
    try {
      // Use Compliance Microservice exclusively for all compliance data
      // Filter by copilot-traces source to get copilot-specific data
      const sourceFilter = {
        source: 'saf3ai-copilot-traces',
        module: 'microsoft-copilot',
      };

      // Time range — honour whatever the UI passes (time_range preset or explicit dates).
      if (params.time_range) {
        sourceFilter.time_range = params.time_range;
      } else if (params.start_time || params.end_time) {
        if (params.start_time) sourceFilter.start_date = params.start_time;
        if (params.end_time) sourceFilter.end_date = params.end_time;
      } else {
        sourceFilter.time_range = 'last_90_days';
      }
      if (params.agent_id) sourceFilter.agent_id = params.agent_id;
      if (params.user_email) sourceFilter.user_email = params.user_email;

      const [frameworksData, summaryData, controlSummaryData] = await Promise.all([
        complianceAPI.getAllFrameworks({ product_scope: 'enterprise_ai', module: 'microsoft-copilot', include_custom: true }),
        complianceAPI.getComplianceSummary(sourceFilter),
        complianceAPI.getControlSummary(sourceFilter),
      ]);

      // Build lookup maps
      const summaryByFramework = {};
      (summaryData?.frameworks || []).forEach(s => {
        summaryByFramework[s.framework_id] = s;
      });

      const controlSummaryById = {};
      (controlSummaryData?.controls || []).forEach(c => {
        controlSummaryById[c.control_id] = c;
      });

      // Helper functions. A null score is "not assessed", never compliant.
      const getStatus = (score) => {
        if (score == null) return 'not-evaluated';
        if (score >= 90) return 'compliant';
        if (score >= 70) return 'needs-attention';
        return 'non-compliant';
      };
      const getControlStatus = (score) => {
        if (score >= 90) return 'pass';
        if (score >= 70) return 'warning';
        return 'fail';
      };

      // Framework icon mapping
      const frameworkIcons = {
        'ISO 42001': 'award',
        'EU AI Act': 'globe',
        'NIST AI RMF': 'shield',
        'Internal Policy': 'file-text',
        'SOC 2': 'check-circle',
        'GDPR': 'lock',
        'HIPAA': 'heart',
      };

      // Process frameworks with real compliance data
      let frameworks = [];
      const frameworksList = Array.isArray(frameworksData) ? frameworksData : (frameworksData?.frameworks || []);
      
      if (frameworksList.length > 0) {
        frameworks = frameworksList.map(fw => {
          const fwSummary = summaryByFramework[fw.framework_id];
          
          // Enrich controls with real violation data from OpenSearch
          const controls = (fw.controls || []).map(ctrl => {
            const ctrlSummary = controlSummaryById[ctrl.control_id];
            const violations = ctrlSummary?.violations ?? 0;
            const totalScanned = ctrlSummary?.total_scanned ?? 0;
            // No evidence → not assessed. A missing summary row (the control
            // summary returns a row for every control, so a gap means it
            // failed or the ids drifted) and a row with
            // compliance_percentage: null (CMP-155: nothing can test it) both
            // stay null. They used to score 100% "pass" (audit B6).
            const notTestable = !ctrlSummary || ctrlSummary.compliance_percentage == null;
            const compliancePct = notTestable ? null : Number(ctrlSummary.compliance_percentage);

            return {
              id: `${fw.framework_id}-${ctrl.control_id}`,
              control_id: ctrl.control_id,
              control_number: ctrl.control_number,
              name: ctrl.name,
              description: ctrl.description,
              remediation_guidance: ctrl.remediation_guidance,
              status: notTestable ? 'not-evaluated' : getControlStatus(compliancePct),
              score: compliancePct == null ? null : Math.round(compliancePct),
              violations: violations,
              total_scanned: totalScanned,
            };
          });

          // Controls passing / controls evaluated, from the same rows the UI
          // renders (as Copilot Studio and Gemini Enterprise score it). Not-
          // evaluated controls are outside the denominator; a framework with
          // no evaluated control is not assessed (null), never 100.
          const evaluatedControls = controls.filter(c => c.score != null);
          const passingControls = evaluatedControls.filter(c => c.status === 'pass').length;
          const frameworkScore = evaluatedControls.length > 0
            ? Math.round((passingControls / evaluatedControls.length) * 100)
            : null;

          const totalViolations = fwSummary?.total_violations ??
            controls.reduce((sum, c) => sum + c.violations, 0);

          return {
            id: fw.framework_id,
            name: fw.name,
            fullName: fw.full_name || fw.name,
            description: fw.description || `Compliance framework: ${fw.name}`,
            icon: frameworkIcons[fw.name] || 'clipboard',
            score: frameworkScore,
            status: getStatus(frameworkScore),
            passingControls,
            // Denominator of the score (evaluated controls); the full control
            // count stays available as controlCount.
            totalControls: evaluatedControls.length,
            controlCount: controls.length,
            violations: totalViolations,
            controls,
            isCustom: Boolean(fw.is_custom),
          };
        });
      }

      // An empty catalogue stays empty. This used to inject "ISO 42001" and
      // "EU AI Act" scored 100 / compliant with zero controls (audit B6).

      // Only certified frameworks drive the headline; custom rules are
      // reported separately (same model as Copilot Studio / Gemini Enterprise).
      const summarize = (group) => {
        const total = group.reduce((sum, f) => sum + f.totalControls, 0);
        const passing = group.reduce((sum, f) => sum + f.passingControls, 0);
        const score = total > 0 ? Math.round((passing / total) * 100) : null;
        return {
          overall_score: score,
          overall_status: getStatus(score),
          framework_count: group.length,
          passing_controls: passing,
          total_controls: total,
          total_violations: group.reduce((sum, f) => sum + (Number(f.violations) || 0), 0),
        };
      };
      const certified = summarize(frameworks.filter(f => !f.isCustom));
      const custom = summarize(frameworks.filter(f => f.isCustom));

      // Use backend-provided aggregates directly to avoid frontend double-counting.
      const totalViolations = summaryData?.total_violations ?? frameworks.reduce((sum, f) => sum + f.violations, 0);
      const totalScannedInteractions = summaryData?.total_scanned ?? 0;

      return {
        frameworks,
        overall_score: certified.overall_score,
        overall_status: certified.overall_status,
        total_controls: certified.total_controls,
        passing_controls: certified.passing_controls,
        total_violations: totalViolations,
        certified: { ...certified, total_scanned: totalScannedInteractions },
        custom: { ...custom, total_scanned: totalScannedInteractions },
        score_basis: 'certified-controls-met',
        recent_violations: [],
        metrics: {
          total_interactions: totalScannedInteractions,
          total_scanned: totalScannedInteractions,
          // Not measured here: there is no interactions-in-scope denominator
          // in this call (was a hardcoded 100). Null renders as "—".
          scan_coverage: null,
          threats_detected: null,
          pii_detected: null,
          sensitive_docs: null,
        },
        pii_exposures: null,
        high_risk_intents: null,
        last_updated: new Date().toISOString(),
      };
    } catch (error) {
      console.error('Error fetching AI compliance:', error);
      throw error;
    }
  },

  /**
   * Get audit logs with filtering
   */
  async getAuditLogs(params = {}) {
    try {
      const data = await enterpriseAIAPI.getAuditLogs({
        page: params.page || 1,
        page_size: params.page_size || 50,
        search: params.search,
        event_type: params.eventType,
        severity: params.severity,
        status: params.status,
        user: params.user,
        resource: params.resource,
        start_time: params.start_time,
        end_time: params.end_time,
      });

      return {
        logs: (data.logs || []).map(log => ({
          ...log,
          agent_name: log.agent_name || getAppDisplayName(log.application),
          threat_severity: log.threat_severity || log.severity || null,
          risk_level: log.risk_level || log.intent_risk_level || null,
          scan_status: log.scan_status || null,
          // M365 does not currently report call outcomes. Preserve that as
          // null instead of manufacturing SUCCEEDED from scan completion.
          state: log.state || null,
        })),
        total: data.total || 0,
        page: data.page || 1,
        page_size: data.page_size || 50,
        total_pages: data.total_pages || 1,
        // The M365 feed has no agent-lifecycle or user-feedback events, so
        // those Log Explorer tiles are not measured for this source (null →
        // "—"), not counted as 0 (audit A17).
        stats: data.stats ? { ...data.stats, agent_events: null, user_events: null } : null,
      };
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  },

  /**
   * Get audit log filter options
   */
  async getAuditLogFilters() {
    try {
      return await enterpriseAIAPI.getAuditLogFilters();
    } catch (error) {
      console.error('Error fetching audit log filters:', error);
      throw error;
    }
  },

  // -------------------------------------------------------------------------
  // Methods added for Gemini-Enterprise-style sub-pages (conversations,
  // posture, sources). M365 Copilot's interactions endpoint already
  // returns prompt+response pairs, so getConversations is a thin
  // adapter. Posture + sources hit endpoints that don't exist on the
  // copilot microservice yet — they return empty results gracefully so
  // the new pages render without errors. TODO follow-up: add
  // /api/v1/copilot/sources, /api/v1/copilot/posture/metrics,
  // /api/v1/copilot/posture/findings on the saf3ai-enterprise-ai-microservice.
  // -------------------------------------------------------------------------

  async getConversations(params = {}) {
    try {
      const data = await enterpriseAIAPI.getInteractions({
        page: params.page || 1,
        page_size: params.page_size || params.limit || 50,
        user_email: params.user_email || undefined,
        application: params.application || undefined,
        start_time: params.start_time || params.startTime || undefined,
        end_time: params.end_time || params.endTime || undefined,
        intent: params.intent || undefined,
        severity: params.severity || undefined,
        search: params.search || undefined,
      });
      return {
        conversations: (data.interactions || data.items || []),
        total: data.total || 0,
        page: data.page || 1,
        page_size: data.page_size || 50,
      };
    } catch (error) {
      console.error('Error fetching Copilot conversations:', error);
      return { conversations: [], total: 0, page: 1, page_size: 50 };
    }
  },

  // -------------------------------------------------------------------------
  // Sessions — the gemini conversations component groups interactions
  // into sessions (one row per conversation thread). copilotAPI already
  // exposes /copilot/sessions and /copilot/sessions/{id}; these are
  // thin pass-throughs so the gemini component "just works" via the
  // injected service.
  // -------------------------------------------------------------------------

  async getSessions(params = {}) {
    try {
      const data = await enterpriseAIAPI.getSessions(params);
      // Map M365 backend shape → gemini conversations component shape.
      // The shared component reads ``session.agent_name`` and
      // ``session.last_activity``; our backend returns ``application``
      // and ``end_time``. Run application through getAppDisplayName so
      // old spans with raw IPM.SkypeTeams.* strings still display
      // friendly names ("Microsoft 365 Chat") in the table.
      const sessions = (data?.sessions || []).map(s => {
        const friendly = getAppDisplayName(s.application) || s.application || 'Microsoft 365 Chat';
        // Microsoft's Graph API doesn't expose a success/failure flag per
        // interaction, so session completion is not measured: null, never
        // "100 if no threats else 0" (audit B10). The Session Completion
        // card is hidden for M365 (showConversationCompletion: false).
        return {
          ...s,
          application: friendly,
          agent_name: friendly,
          last_activity: s.end_time || s.last_activity || s.start_time,
          success_rate: s.success_rate ?? null,
        };
      });
      return { ...data, sessions };
    } catch (error) {
      console.error('Error fetching Copilot sessions:', error);
      return { sessions: [], total: 0, page: 1, page_size: 50 };
    }
  },

  async getSessionDetails(sessionId) {
    try {
      const data = await enterpriseAIAPI.getSessionDetails(sessionId);
      // Backend shape mismatch: copilot's /sessions/{id} returns
      // ``interactions`` as a count (integer), gemini's returns an
      // array of message records. The gemini conversations component
      // calls ``.filter()`` on it. Normalize so a non-array always
      // becomes [], and try alternative array fields the backend
      // may have used (``messages``, ``items``, ``records``).
      const arrayLike =
        Array.isArray(data?.interactions) ? data.interactions
        : Array.isArray(data?.messages) ? data.messages
        : Array.isArray(data?.items) ? data.items
        : Array.isArray(data?.records) ? data.records
        : [];
      // Each interaction in the timeline shows
      // ``interaction.agent_name || labels.responseSenderFallback``.
      // Map our ``application`` → ``agent_name`` so the real value
      // (e.g. "Asana", "PowerPoint", "News Q&A") shows instead of
      // the fallback "Copilot".
      const interactions = arrayLike.map(i => ({
        ...i,
        agent_name: i.agent_name || i.application || i.app_name || null,
      }));
      return { ...data, interactions };
    } catch (error) {
      console.error('Error fetching Copilot session details:', error);
      // Return an empty-but-valid shape so the panel renders an
      // empty-state instead of crashing the UI.
      return { interactions: [] };
    }
  },

  // Recommendations — rule-based suggestions for dashboard / users page.
  // Backed by /copilot/api/v1/recommendations which evaluates a small set
  // of rules over the same dashboard / users / threats / knowledge-gaps
  // signals. Returns empty on error so consumers render gracefully.
  async getRecommendations(_params = {}) {
    // /copilot/api/v1/recommendations is not implemented on the M365 backend.
    // Return empty so consumers render gracefully without making a failing request.
    return { recommendations: [], total: 0 };
  },

  // Configuration — org-level settings (ROI rates, license cost, etc.)
  // Stored in MySQL via /api/v1/copilot/config (ConfigRepository).
  async getConfig(organizationId) {
    try {
      const orgId = organizationId || _resolveOrgId();
      const data = await enterpriseAIAPI.getConfig(orgId);
      return data?.config || data;
    } catch (error) {
      console.error('Error fetching Copilot config:', error);
      return null;
    }
  },

  async updateConfig(organizationId, configData) {
    const orgId = organizationId || _resolveOrgId();
    return await enterpriseAIAPI.updateConfig(orgId, configData);
  },

  async resetConfig(organizationId) {
    const orgId = organizationId || _resolveOrgId();
    return await enterpriseAIAPI.resetConfig(orgId);
  },

  // Adoption metrics — derive a date-keyed daily series from the
  // dashboard timeline. The shared usage-roi page reads
  // ``adoption.daily_active_users`` to draw the Adoption Trend chart;
  // without this, the chart renders flat zero. Each bucket carries
  // both the interaction count and unique_users for that day.
  async getAdoptionMetrics(params = {}) {
    try {
      const data = await enterpriseAIAPI.getDashboard(params);
      const timeline = (data?.timeline || []).map(b => ({
        date: (b.date || '').slice(0, 10),
        interactions: b.count || 0,
        // Per-day distinct users from the backend's daily cardinality
        // sub-aggregation. Null when a point lacks it — never the window
        // total repeated on every day (audit B13).
        unique_users: b.unique_users ?? null,
      })).filter(d => d.date);
      return {
        daily_active_users: timeline,
        // Hourly distribution stays in the gemini ROI fallback path —
        // we don't override it here.
      };
    } catch (err) {
      console.error('Error fetching adoption metrics:', err);
      return null;
    }
  },

  // Security Summary severity tiles — derived from the dashboard severity
  // distribution. The dashboard reads critical / high / medium / low; this
  // used to return severity buckets renamed as threat types (data_exfil =
  // critical, injection_attempts = high, …), so every tile showed 0 (audit
  // A1/B14). A failure is thrown so the dashboard does not show zeros as data.
  async getThreatStats(params = {}) {
    const timeParams = {};
    if (params.start_time) timeParams.start_time = params.start_time;
    if (params.end_time) timeParams.end_time = params.end_time;
    const data = await enterpriseAIAPI.getDashboard(timeParams);
    const sd = data?.severity_distribution || {};
    const critical = Number(sd.critical) || 0;
    const high = Number(sd.high) || 0;
    const medium = Number(sd.medium) || 0;
    const low = Number(sd.low) || 0;
    return {
      total_flagged: critical + high + medium + low,
      critical,
      high,
      medium,
      low,
    };
  },

  // Compliance summary — wraps getAICompliance defined below in a shape
  // the shared agents pages expect (`stats`, `frameworks`, and `violations`).
  // A failure is thrown, so the page shows its error state and the dashboard
  // badge reads "—" — it used to resolve to a 0% "compliant" score.
  async getCompliance(params = {}) {
    const data = await this.getAICompliance(params);
    return {
      stats: {
        // null = not assessed (no evaluated certified control).
        overall_score: data?.overall_score ?? null,
        overall_status: data?.overall_status ?? 'not-evaluated',
        passing_controls: data?.passing_controls ?? 0,
        total_controls: data?.total_controls ?? 0,
        total_violations: data?.total_violations ?? 0,
        scan_coverage: data?.scan_coverage ?? data?.metrics?.scan_coverage ?? null,
        total_scanned: data?.total_scanned ?? data?.metrics?.total_scanned ?? 0,
        certified: data?.certified,
        custom: data?.custom,
        score_basis: data?.score_basis,
      },
      frameworks: data?.frameworks ?? [],
      violations: data?.recent_violations ?? [],
    };
  },

  // Adapter — translates the M365 sources backend shape into the
  // gemini-enterprise sources shape so the shared
  // ``pages/gemini/agents/sources`` page can render Copilot data
  // identically. The M365 backend returns:
  //   { name, document_type, type, url, host, reference_count,
  //     unique_users, applications, last_accessed }
  // The shared page expects:
  //   { title, uri, domain, source_type, is_external,
  //     citation_count, last_cited, agent_names }
  async getSources(params = {}) {
    try {
      const data = await enterpriseAIAPI.getSources(params);
      const raw = Array.isArray(data?.sources) ? data.sources : [];

      // Tenant-internal Microsoft destinations. Two checks:
      //  - friendly-name match (what our normalizer's _get_host_type
      //    returns for known platforms)
      //  - hostname-suffix match (catches everything Microsoft serves
      //    on these domains, including subdomains we don't enumerate)
      const FRIENDLY_INTERNAL = new Set([
        'sharepoint', 'onedrive', 'teams', 'outlook',
        'onenote', 'office', 'office 365', 'microsoft 365',
        'exchange', 'yammer', 'viva', 'stream', 'forms',
        'planner', 'whiteboard', 'todo', 'to do',
        'power bi', 'power apps', 'power automate', 'dynamics',
        'azure', 'copilot', 'bing chat', 'm365 chat', 'bizchat',
      ]);
      const MS_HOST_SUFFIXES = [
        '.sharepoint.com', '.onedrive.com', '.office.com', '.office365.com',
        '.microsoft.com', '.microsoftonline.com', '.live.com',
        '.azure.com', '.azurewebsites.net', '.windows.net',
        '.dynamics.com', '.powerbi.com', '.powerapps.com', '.powerplatform.com',
        '.outlook.com', '.exchange.com', '.onenote.com', '.yammer.com',
        '.bing.com',
        // bare domains
      ];
      const MS_HOST_EXACT = new Set([
        'sharepoint.com', 'onedrive.com', 'office.com', 'office365.com',
        'microsoft.com', 'microsoftonline.com', 'live.com',
        'azure.com', 'dynamics.com', 'powerbi.com', 'outlook.com',
        'onenote.com', 'yammer.com', 'bing.com',
      ]);
      const isMicrosoftHost = (h) => {
        if (!h) return false;
        const hl = h.toLowerCase();
        if (FRIENDLY_INTERNAL.has(hl)) return true;
        if (MS_HOST_EXACT.has(hl)) return true;
        return MS_HOST_SUFFIXES.some(suf => hl.endsWith(suf));
      };

      const sources = raw.map((s) => {
        const ext = ((s?.type || s?.document_type) || '').toLowerCase();
        const host = s?.host || 'Microsoft 365';
        const isExternal = !isMicrosoftHost(host);
        // ``source_type`` is the human-readable category the gemini
        // page renders in chips and groups by. File family takes
        // precedence; external links without an extension fall back
        // to "Web".
        const sourceType =
          ext === 'doc' || ext === 'docx' ? 'Word' :
          ext === 'xls' || ext === 'xlsx' ? 'Excel' :
          ext === 'ppt' || ext === 'pptx' ? 'PowerPoint' :
          ext === 'pdf' ? 'PDF' :
          ext === 'one' || ext === 'onetoc2' ? 'OneNote' :
          ext === 'msg' || ext === 'eml' ? 'Email' :
          isExternal ? 'Web' :
          'Document';
        return {
          ...s,
          title: s?.name || s?.title || '(untitled)',
          uri: s?.url || s?.uri || s?.name || '',
          domain: host,
          source_type: sourceType,
          is_external: isExternal,
          citation_count: s?.reference_count ?? s?.citation_count ?? 0,
          interaction_count: s?.reference_count ?? 0,
          last_cited: s?.last_accessed || s?.last_cited || null,
          agent_names: Array.isArray(s?.applications) ? s.applications : [],
        };
      });

      const stats = data?.stats || {};
      return {
        ...data,
        sources,
        total: data?.total ?? sources.length,
        // Aliases that the gemini sources page also reads.
        stats: {
          ...stats,
          total_sources: stats.total_documents ?? sources.length,
          total_citations: stats.total_references
            ?? sources.reduce((a, s) => a + (s.citation_count || 0), 0),
        },
      };
    } catch (error) {
      if (error?.response?.status !== 404) {
        console.error('Error fetching Copilot sources:', error);
      }
      return { sources: [], total: 0, stats: {} };
    }
  },

  // Grounding analytics — the shared gemini-enterprise sources page
  // ``Promise.allSettled``s this alongside getSources/getRecommendations.
  // Calling an undefined method synchronously throws TypeError BEFORE
  // allSettled can absorb it, which prevents the sources from rendering
  // at all (the user sees an empty page). Return an empty-shape result
  // so the call resolves cleanly until we have a real M365-side
  // grounding analytics endpoint.
  async getGroundingAnalytics(_params = {}) {
    return { domain_breakdown: [], total_citations: 0, total_grounded: 0 };
  },

  async getPostureMetrics() {
    try {
      return await enterpriseAIAPI.getPostureMetrics();
    } catch (err) {
      console.error('Error fetching Copilot posture metrics:', err);
      return {
        total_findings: 0, critical: 0, high: 0, medium: 0, low: 0, info: 0,
        total_permission_findings: 0, total_secret_findings: 0, total_ca_findings: 0,
        by_type: {}, last_scan_at: null, enabled: true, running: false,
      };
    }
  },

  async getPostureFindings(params = {}) {
    try {
      return await enterpriseAIAPI.getPostureFindings(params);
    } catch (err) {
      // Rethrown so the page shows its error state, not "no findings" (C17).
      console.error('Error fetching Copilot posture findings:', err);
      throw err;
    }
  },

  // Agent / user picker options: GET /filters → `{ agents, users }`, the distinct
  // values active in the window (raw gen_ai.application values and emails). The
  // service ignores the scope for this call, so a narrowed page still lists
  // everything it could be narrowed to.
  // A failure is thrown, so the pickers show an error rather than an empty list.
  async getSessionFilters(params = {}) {
    const tp = {};
    if (params.start_time) tp.start_time = params.start_time;
    if (params.end_time) tp.end_time = params.end_time;
    try {
      const data = await enterpriseAIAPI.getFilters(tp);
      return { agents: uniqueStrings(data?.agents), users: uniqueStrings(data?.users) };
    } catch (err) {
      if (err?.response?.status !== 404) throw err;
      // Fallback ONLY for an older deploy that has no GET /filters (404): the
      // compliance service's agent/user lists for this module. Those cover
      // compliance impacts only, so a quiet app or user may be missing — remove
      // this branch once every environment runs the /filters build.
      const legacy = {};
      if (tp.start_time) legacy.start_date = tp.start_time;
      if (tp.end_time) legacy.end_date = tp.end_time;
      if (!legacy.start_date && !legacy.end_date) legacy.time_range = 'last_90_days';
      const [agentsRes, usersRes] = await Promise.all([
        complianceAPI.getAgents({ module: 'microsoft-copilot', ...legacy }),
        complianceAPI.getUsers({ module: 'microsoft-copilot', ...legacy }),
      ]);
      return {
        agents: uniqueStrings((agentsRes?.agents || []).map((a) => a.agent_name || a.agent_display_name || a.agent_id)),
        users: uniqueStrings((usersRes?.users || []).map((u) => u.user_email)),
      };
    }
  },

  // AI-driven conversation drift analysis. Sends the session's user
  // prompt + AI chunked responses to the AI microservice, which runs
  // Gemini to detect drift / hallucination / risk signals. Cached
  // server-side by session_id so repeated opens are instant.
  async getConversationDriftAnalysis({ session_id, user_prompt, ai_responses, application, user_email, content_status, force_regenerate = false }) {
    try {
      return await enterpriseAIAPI.analyzeConversationDrift({
        session_id,
        user_prompt,
        ai_responses,
        application,
        user_email,
        content_status,
      }, { force_regenerate });
    } catch (err) {
      console.error('Error fetching drift analysis:', err);
      return { ai_summary: null, error: err?.message || 'Failed to load drift analysis' };
    }
  },
};

export default copilotService;
